/*
 * Slider — Swiper-backed carousel for the `slider` block.
 *
 * Block markup contract (set by blocks/slider.liquid):
 *   slider-component[data-…]
 *     .slider__viewport
 *       .swiper
 *         .swiper-wrapper
 *           <direct children>  — wrapped in .swiper-slide here, at init
 *       .slider__arrows.slider__arrows--absolute
 *         .slideshow-control.slideshow-control--previous
 *         .slideshow-control.slideshow-control--next
 *     .slider__arrows.slider__arrows--flow
 *       .slideshow-control.slideshow-control--previous
 *       .slideshow-control.slideshow-control--next
 *     .slider__pagination.swiper-pagination
 *
 * Both arrow containers (absolute + flow) hold their own prev/next
 * buttons; CSS shows only the active one per breakpoint. Swiper's
 * native navigation config only supports a single prev / next element,
 * so this module skips that config and binds clicks manually on EVERY
 * .slideshow-control--previous / --next inside the slider — taps on
 * either container drive the same Swiper instance.
 *
 * Per-instance pattern (one Swiper per slider-component). Re-runs on
 * shopify:section:load so customizer re-renders pick up cleanly.
 *
 * Swiper is loaded as a UMD bundle by a <script> tag in
 * snippets/scripts.liquid earlier in the document, exposing a global
 * `Swiper` constructor.
 */

const Swiper = window.Swiper;

const SELECTOR = 'slider-component';
const INSTANCE_KEY = '_sliderInstance';

class Slider {
  constructor(root) {
    this.root = root;
    this.swiper = null;
    this.handlers = [];

    this.init();
  }

  init() {
    const swiperEl = this.root.querySelector('.swiper');
    const wrapper = swiperEl?.querySelector('.swiper-wrapper');
    if (!swiperEl || !wrapper) return;

    // Slides are `slider-slide` block instances that emit
    // `.swiper-slide` themselves — no init-time tagging needed.
    // Belt-and-braces: also tag any direct children that didn't
    // already get the class, so unexpected DOM shapes (e.g. a static
    // template using a non-slide block by mistake) still scroll
    // instead of laying out broken.
    Array.from(wrapper.children).forEach((child) => {
      if (!child.classList.contains('swiper-slide')) {
        child.classList.add('swiper-slide');
      }
    });

    const ds = this.root.dataset;
    const spvD = parseFloat(ds.spvD) || 3;
    const spvT = parseFloat(ds.spvT) || 2;
    const spvM = parseFloat(ds.spvM) || 1.2;
    const gapD = parseFloat(ds.gapD) || 16;
    const gapT = parseFloat(ds.gapT) || 16;
    const gapM = parseFloat(ds.gapM) || 12;
    const loop = ds.loop === 'true';
    const autoplay = ds.autoplay === 'true';
    const autoplayDelay = (parseFloat(ds.autoplayDelay) || 5) * 1000;

    // Pagination is per-breakpoint via the schema. Swiper's pagination
    // config is global — we enable it when ANY breakpoint requests
    // bullets and rely on CSS (driven by data-show-pagination-{d,t,m})
    // to hide the container at breakpoints where the merchant chose
    // false. Same approach for arrows: Swiper just powers the
    // navigation; CSS picks which container shows.
    const anyPagination = [
      ds.showPaginationD,
      ds.showPaginationT,
      ds.showPaginationM,
    ].some((v) => v === 'true');

    /** @type {any} */
    const options = {
      // Mobile-first defaults; breakpoints below override at >=768
      // (tablet) and >=992 (desktop). Matches the 991 / 992 boundary
      // convention used by the rest of the theme.
      slidesPerView: spvM,
      spaceBetween: gapM,
      loop,
      autoplay: autoplay
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        768: { slidesPerView: spvT, spaceBetween: gapT },
        992: { slidesPerView: spvD, spaceBetween: gapD },
      },
      effect: ds.effect || 'slide',
      fadeEffect: { crossFade: true },
      watchOverflow: false,
      observer: true,
      observeParents: true,
    };

    if (anyPagination) {
      const paginationEl = this.root.querySelector('.slider__pagination');
      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }

    this.swiper = new Swiper(swiperEl, options);
    this.root[INSTANCE_KEY] = this;

    // Manual navigation binding — both arrow containers (.slider__arrows--absolute
    // and .slider__arrows--flow) hold their own prev / next buttons. Swiper's
    // native navigation config only supports a single element per direction, so
    // we wire clicks on every button directly to the Swiper instance instead.
    this.root.querySelectorAll('.slideshow-control--previous').forEach((btn) => {
      const handler = (e) => {
        e.preventDefault();
        this.swiper?.slidePrev();
      };
      btn.addEventListener('click', handler);
      this.handlers.push({ btn, handler });
    });
    this.root.querySelectorAll('.slideshow-control--next').forEach((btn) => {
      const handler = (e) => {
        e.preventDefault();
        this.swiper?.slideNext();
      };
      btn.addEventListener('click', handler);
      this.handlers.push({ btn, handler });
    });

    if (!loop) {
      this.prevBtns = this.root.querySelectorAll('.slideshow-control--previous');
      this.nextBtns = this.root.querySelectorAll('.slideshow-control--next');
      const updateArrows = () => {
        const s = this.swiper;
        if (!s) return;
        this.prevBtns.forEach((b) => b.classList.toggle('swiper-button-disabled', s.isBeginning));
        this.nextBtns.forEach((b) => b.classList.toggle('swiper-button-disabled', s.isEnd));
      };
      this.swiper.on('slideChange', updateArrows);
      this.swiper.on('init', updateArrows);
      updateArrows();
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.handlers.forEach(({ btn, handler }) => {
      btn.removeEventListener('click', handler);
    });
    this.handlers = [];
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    // Tear down any prior instance so a customizer re-render doesn't
    // leave a zombie Swiper attached to recycled DOM.
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new Slider(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
